import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';
import { APP_CONSTANTS, APP_URL } from '../../../utils/AppConstants';

// Constants
const OK = 200;
export const DESTINATIONS = {
    manageMyBenefits: 'manageMyBenefits',
    memberPortal: 'memberPortal',
    planMember: 'planMember',
    gbClaims: 'gbClaims',
};

const { MEMBER_PORTAL_HOST, MEMBER_PORTAL_TRANSFER_IN_PATH, GB_BFF_HOST } = APP_URL;

// Maps destination to appropriate transfer URL
const destinationMap = {
    manageMyBenefits: window.__ENV__.MMB_TRANSFER_URL,
    memberPortal: `${MEMBER_PORTAL_HOST}${MEMBER_PORTAL_TRANSFER_IN_PATH}`,
    planMember: window.__ENV__.PLAN_MEMBER_TRANSFER_URL,
    gbClaims: window.__ENV__.CLAIMS_TRANSFER_URL,
};

// Action names
const ACTION_NAME_PREFIX = 'action/transferOutWithToken';
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Exchanges a JWT for a transfer token and redirects the user to the specified destination site
 * @param {string} destination
 * @param {string} path
 * @param {string} step
 * @param {string} target
 * @param {object} logger
 */
export const TransferOutWithToken =
    (destination, path, step, target) =>
    async (dispatch, getState, { logger }) => {
        if (!Object.hasOwn(destinationMap, destination)) {
            return dispatch({
                type: ERROR_ACTION_NAME,
                code: 'N/A',
                message: 'Bad Request: Specified transfer destination is invalid. Error unrelated to GB Core.',
            });
        }

        const { jwt, sessionCorrelationId } = getState().Authentication;
        const { ctxQueryString } = getState().Context;
        const transactionId = logger.generateGuid();

        logger.info(`${ACTION_NAME_PREFIX}: Calling the BFF to exchange JWT for a transfer token.`, {
            correlation: { transactionId },
        });

        const response = await fetch(`${GB_BFF_HOST}/transfer/v1/out`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'mli-session-correlation-id': sessionCorrelationId,
                'mli-correlation-id': transactionId,
                'mli-channel': 'web',
            },
            body: JSON.stringify({
                tokens: {
                    'gb-authorization': jwt,
                },
            }),
        });

        const responseBody = await response.json();

        if (response.status === OK) {
            const transferToken = responseBody['gb-transfer-token'];

            // Check available transfer parameters from browser session storage
            let params = {};
            const origin = sessionStorage.getItem(APP_CONSTANTS.MPROFILE_ORIGIN);
            params.token = transferToken;

            // Determine the origin and set corresponding parameters
            switch (origin) {
                case 'memberPortal':
                    params.origin = 'memberPortal';
                    break;
                case 'planMember':
                    params.origin = 'planMember';
                    break;
                case 'gbClaims':
                    params.origin = 'gbClaims';
                    params.claimstep = step;
                    break;
                default:
                    break;
            }

            // Add ctx when context exists
            if (ctxQueryString) {
                params.ctx = ctxQueryString;
            }
            if (path) {
                params.path = path;
            }

            const extraQueryStrings = new URLSearchParams(params).toString();

            const finalDestination = `${destinationMap[destination]}?${extraQueryStrings}`;

            window.open(finalDestination, target);
        } else {
            logger.error(
                `${ACTION_NAME_PREFIX}: Transfer failed. Code: ${responseBody.code} Reason: ${responseBody.message}`,
                { correlation: { transactionId } }
            );

            return dispatch({
                type: ERROR_ACTION_NAME,
                code: responseBody.code,
                message: responseBody.message,
            });
        }
    };

/**
 * Fired off when the request encounters an error.
 * failure.
 * @param {object} state
 * @param {object} action
 */
export const errorReducer = produce((state, action) => {
    state.Errors.transfer.hasError = true;
    state.Errors.transfer.code = action.code;
    state.Errors.transfer.message = action.message;
});

// Register Reducers with Redux root reducer
registerReducer(ERROR_ACTION_NAME, errorReducer);
